import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/query/query-keys';
import type { TripDetail, TripDetailCity } from '@/hooks/trips/types';

interface ToggleHotelBookingInput {
  tripId: string;
  cityId: number;
  hotelId: number;
  isBooked: boolean;
}

async function toggleHotelBooking(hotelId: number, isBooked: boolean): Promise<void> {
  const response = await apiFetch(`/api/v1/business-trip-hotels/${hotelId}/book`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      is_booked: isBooked,
    }),
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const message =
      payload && typeof payload === 'object' && 'error' in (payload as Record<string, unknown>)
        ? String(
            (payload as Record<string, { message?: string }>).error?.message ??
              'Failed to update hotel booking status'
          )
        : 'Failed to update hotel booking status';

    throw new Error(message);
  }
}

export function useToggleHotelBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ hotelId, isBooked }: ToggleHotelBookingInput) =>
      toggleHotelBooking(hotelId, isBooked),
    onMutate: async ({ tripId, cityId, hotelId, isBooked }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.trip(tripId) });
      await queryClient.cancelQueries({ queryKey: queryKeys.tripCities(tripId) });
      await queryClient.cancelQueries({ queryKey: queryKeys.tripCity(tripId, cityId) });

      const tripKey = queryKeys.trip(tripId);
      const cityListKey = queryKeys.tripCities(tripId);
      const cityDetailKey = queryKeys.tripCity(tripId, cityId);

      const previousTrip = queryClient.getQueryData<TripDetail>(tripKey);
      const previousCityList = queryClient.getQueryData<TripDetailCity[]>(cityListKey);
      const previousCityDetail = queryClient.getQueryData<TripDetailCity>(cityDetailKey);

      if (previousTrip) {
        const updatedTrip: TripDetail = {
          ...previousTrip,
          cities: previousTrip.cities?.map((city) => {
            if (city.id !== cityId) {
              return city;
            }

            return {
              ...city,
              hotels: city.hotels.map((hotel) =>
                hotel.id === hotelId ? { ...hotel, isBooked } : hotel
              ),
            };
          }),
        };
        queryClient.setQueryData(tripKey, updatedTrip);
      }

      if (previousCityList) {
        const updatedCities = previousCityList.map((city) => {
          if (city.id !== cityId) {
            return city;
          }
          return {
            ...city,
            hotels: city.hotels.map((hotel) =>
              hotel.id === hotelId ? { ...hotel, isBooked } : hotel
            ),
          };
        });
        queryClient.setQueryData(cityListKey, updatedCities);
      }

      if (previousCityDetail) {
        const updatedCityDetail: TripDetailCity = {
          ...previousCityDetail,
          hotels: previousCityDetail.hotels.map((hotel) =>
            hotel.id === hotelId ? { ...hotel, isBooked } : hotel
          ),
        };
        queryClient.setQueryData(cityDetailKey, updatedCityDetail);
      }

      return { previousTrip, previousCityList, previousCityDetail };
    },
    onError: (_error, { tripId, cityId }, context) => {
      if (!context) {
        return;
      }

      const tripKey = queryKeys.trip(tripId);
      const cityListKey = queryKeys.tripCities(tripId);
      const cityDetailKey = queryKeys.tripCity(tripId, cityId);

      if (context.previousTrip) {
        queryClient.setQueryData(tripKey, context.previousTrip);
      }

      if (context.previousCityList) {
        queryClient.setQueryData(cityListKey, context.previousCityList);
      }

      if (context.previousCityDetail) {
        queryClient.setQueryData(cityDetailKey, context.previousCityDetail);
      }
    },
    onSettled: (_result, _error, { tripId, cityId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trip(tripId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tripCities(tripId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tripCity(tripId, cityId) });
    },
  });
}
